import { c as _c } from "react/compiler-runtime";
import './text-area.scss';
import { type TextareaHTMLAttributes, useRef, useState } from 'react';
import type { FieldProps } from '../field/field.tsx';
import { Field, FieldErrorMessage, FieldWrapper, getErrorElId } from '../field/field.tsx';
import { clampToLineBudget, computeFillPercent, newlineOverflowsBudget } from './text-area.logic.ts';
export type TextAreaProps = {
  'aria-labelledby'?: React.ComponentPropsWithoutRef<'textarea'>['aria-labelledby'];
  'aria-label'?: string;
  label?: string;
  name?: string;
  className?: string;
  placeholder?: string;
  defaultValue?: string;
  onChange?: TextareaHTMLAttributes<HTMLTextAreaElement>['onChange'];
  textareaRef?: React.MutableRefObject<HTMLTextAreaElement>;
  maxLength?: number;
  rows?: number;
  helpText?: string;
  /** Max visual lines the value may occupy; input is clamped to fit. */
  maxLines?: number;
  /** Characters per visual line, used to estimate wrapping. Defaults to 40. */
  charsPerLine?: number;
  /** `numeric` shows `charCount/maxLength`; `percentage` shows how full. */
  counterMode?: 'numeric' | 'percentage';
  /**
   * Renders the capacity label in `percentage` mode from the live fill percent.
   * Lets the caller localize the message in its own namespace. When omitted,
   * the raw fill percent is shown.
   */
  renderCapacityLabel?: (fillPercent: number) => React.ReactNode;
};
export const TextArea: React.FC<TextAreaProps> = t0 => {
  const $ = _c(35);
  let defaultValue;
  let fieldProps;
  let helpText;
  let label;
  let maxLength;
  let maxLines;
  let name;
  let onChange;
  let placeholder;
  let renderCapacityLabel;
  let rows;
  let t1;
  let t2;
  let textareaRef;
  if ($[0] !== t0) {
    ({
      label,
      name,
      placeholder,
      defaultValue,
      onChange,
      textareaRef,
      maxLength,
      rows,
      helpText,
      maxLines,
      charsPerLine: t1,
      counterMode: t2,
      renderCapacityLabel,
      ...fieldProps
    } = t0);
    $[0] = t0;
    $[1] = defaultValue;
    $[2] = fieldProps;
    $[3] = helpText;
    $[4] = label;
    $[5] = maxLength;
    $[6] = maxLines;
    $[7] = name;
    $[8] = onChange;
    $[9] = placeholder;
    $[10] = renderCapacityLabel;
    $[11] = rows;
    $[12] = t1;
    $[13] = t2;
    $[14] = textareaRef;
  } else {
    defaultValue = $[1];
    fieldProps = $[2];
    helpText = $[3];
    label = $[4];
    maxLength = $[5];
    maxLines = $[6];
    name = $[7];
    onChange = $[8];
    placeholder = $[9];
    renderCapacityLabel = $[10];
    rows = $[11];
    t1 = $[12];
    t2 = $[13];
    textareaRef = $[14];
  }
  const charsPerLine = t1 === undefined ? 40 : t1;
  const counterMode = t2 === undefined ? "numeric" : t2;
  const [value, setValue] = useState(defaultValue || "");
  const charCount = value.length;
  const lastSyncedValue = useRef(defaultValue || "");
  const fillPercent = computeFillPercent(value, charsPerLine, maxLines, maxLength);
  const showPercentage = counterMode === "percentage" && (maxLength || maxLines);
  const showNumeric = counterMode === "numeric" && maxLength != null && maxLength > 0;
  const t3 = fieldProps as FieldProps;
  const t4 = name ?? "";
  let t5;
  if ($[15] !== charCount || $[16] !== charsPerLine || $[17] !== defaultValue || $[18] !== fillPercent || $[19] !== helpText || $[20] !== label || $[21] !== maxLength || $[22] !== maxLines || $[23] !== onChange || $[24] !== placeholder || $[25] !== renderCapacityLabel || $[26] !== rows || $[27] !== showNumeric || $[28] !== showPercentage) {
    t5 = t6 => {
      const {
        controlProps,
        error,
        formMethods
      } = t6;
      const errorElId = getErrorElId(controlProps.id);
      const handleValueChange = e => {
        const rawValue = e.target.value;
        const prevValue = lastSyncedValue.current;
        if (maxLines != null && newlineOverflowsBudget(rawValue, prevValue, maxLines, charsPerLine)) {
          e.target.value = prevValue;
          return;
        }
        const nextValue = maxLines == null ? rawValue : clampToLineBudget(rawValue, maxLines, charsPerLine);
        if (nextValue !== rawValue) {
          e.target.value = nextValue;
        }
        lastSyncedValue.current = nextValue;
        setValue(nextValue);
        formMethods?.setValue(controlProps.name as string, nextValue, {
          shouldValidate: true
        });
        if (!onChange) {
          return;
        }
        onChange(e);
      };
      return <>{label && <label className="form-label label" htmlFor={controlProps.id}>{label}</label>}<FieldWrapper><div className="flex flex-col w-full"><textarea className="TextArea form-control" rows={rows} maxLength={maxLength} placeholder={placeholder} {...controlProps} {...error && {
              "aria-errormessage": errorElId
            }} onChange={handleValueChange} defaultValue={defaultValue || ""} />{showNumeric && <span className="TextArea__char-count label-sm flex justify-end" aria-live="polite" aria-atomic="true">{`${charCount}/${maxLength}`}</span>}{showPercentage && <span className="TextArea__char-count label-sm flex justify-end" data-full={fillPercent >= 100 ? "true" : undefined} aria-live="polite" aria-atomic="true">{renderCapacityLabel?.(fillPercent) ?? fillPercent}</span>}</div>{helpText && <em className="block label-sm label-subtle mt-snug-lg">{helpText}</em>}<FieldErrorMessage error={error} id={errorElId} /></FieldWrapper></>;
    };
    $[15] = charCount;
    $[16] = charsPerLine;
    $[17] = defaultValue;
    $[18] = fillPercent;
    $[19] = helpText;
    $[20] = label;
    $[21] = maxLength;
    $[22] = maxLines;
    $[23] = onChange;
    $[24] = placeholder;
    $[25] = renderCapacityLabel;
    $[26] = rows;
    $[27] = showNumeric;
    $[28] = showPercentage;
    $[29] = t5;
  } else {
    t5 = $[29];
  }
  let t6;
  if ($[30] !== t3 || $[31] !== t4 || $[32] !== t5 || $[33] !== textareaRef) {
    t6 = <Field {...t3} name={t4} controlRef={textareaRef}>{t5}</Field>;
    $[30] = t3;
    $[31] = t4;
    $[32] = t5;
    $[33] = textareaRef;
    $[34] = t6;
  } else {
    t6 = $[34];
  }
  return t6;
};